import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { t } from "i18next";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import globalStyle from "../../globalStyle";
import {
  createAdminUser,
  updateAdminUser,
} from "../../services/adminUserApiFn";
import { handleOpenSnackBar } from "../../utils";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import Label from "../Label";
import TextInput from "../TextInputs";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const AddEditAdminUserDialog = forwardRef(({ allRole }, ref) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const userData = useSelector((state) => state.userData.user);

  const [inputValues, setInputValues] = useState({
    name: "",
    email: "",
    contact: "",
    role: "",
    password: "",
  });

  const [inputError, setInputError] = useState({
    name: false,
    email: false,
    isEmailValid: false,
    contact: false,
    role: false,
    password: false,
    isStrongPassword: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        let { data } = params;
        setInputValues((pre) => ({
          ...pre,
          name: data.row.name,
          email: data.row.email,
          contact: data.row.contact,
          role: data.row.role._id,
        }));
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputError({});
    setInputValues({});
  };

  const handleAddEditUser = () => {
    let payload = {
      name: inputValues.name,
      email: inputValues.email,
      contact: inputValues.contact,
      role: inputValues.role,
      password: inputValues.password,
      adminId: userData?.adminId,
    };

    if (paramsData?.type === "add") {
      handleApiCall(createAdminUser, payload, "");
    } else {
      handleApiCall(updateAdminUser, payload, paramsData?.data?.row?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading(true);
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;
    setInputError((prevErr) => ({
      ...prevErr,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
      isStrongPassword: name === "password" ? false : inputError.password,
    }));

    setInputValues((preVal) => ({ ...preVal, [name]: value }));
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {paramsData.type === "add" ? t("addNewUser") : t("editUser")}
          </DialogTitle>

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 5,
              right: 5,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label labelText={t("userName")} htmlFor={"user-name"} required />
              <TextInput
                styleProps={globalStyle.textField}
                id={"user-name"}
                required={true}
                inputError={inputError.name}
                helperText={inputError.name && t("userNameRequired")}
                placeholder={"Enter user name"}
                type={"text"}
                name="name"
                value={inputValues.name}
                onChange={handleChangeInputValues}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                labelText={t("emailAddress")}
                htmlFor={"user_email"}
                required
              />
              <TextInput
                styleProps={globalStyle.textField}
                id={"user_email"}
                required={true}
                inputError={inputError.email || inputError.isEmailValid}
                helperText={
                  inputError.email
                    ? t("emailRequired")
                    : inputError.isEmailValid
                    ? t("invalidEmailFormat")
                    : null
                }
                placeholder={t("enterBusinessEmail")}
                type={"email"}
                name="email"
                value={inputValues.email}
                onChange={handleChangeInputValues}
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                labelText={t("contactNumber")}
                htmlFor={"contact_number"}
                required
              />
              <TextInput
                styleProps={globalStyle.textField}
                id={"contact_number"}
                required={true}
                inputError={inputError.contact}
                helperText={inputError.contact && t("contactNumberRequired")}
                placeholder={"Enter contact number"}
                type={"number"}
                name="contact"
                value={inputValues.contact}
                onChange={handleChangeInputValues}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                labelText={t("assignedRole")}
                htmlFor={"assigned_role"}
                required
              />
              <CustomDropDown
                name={"role"}
                value={inputValues.role}
                onChange={handleChangeInputValues}
                formStyleProps={{
                  "& .MuiOutlinedInput-input": {
                    textTransform: "capitalize !important",
                  },
                }}
              >
                <MenuItem>Select</MenuItem>
                {allRole?.map((v, i) => (
                  <MenuItem
                    key={i}
                    value={v._id}
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {v.role}
                  </MenuItem>
                ))}
              </CustomDropDown>
            </Grid>

            {paramsData.type === "add" && (
              <Grid item xs={12}>
                <Label
                  labelText={t("password")}
                  htmlFor={"password"}
                  required
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  inputError={
                    inputError.password || inputError.isStrongPassword
                  }
                  helperText={
                    inputError.password
                      ? t("passwordRequired")
                      : inputError.isStrongPassword
                      ? t("passwordRequirements")
                      : null
                  }
                  id={"password"}
                  required
                  placeholder={`${t("enter")} ${t("password")}`}
                  type={showPass ? "text" : "password"}
                  name="password"
                  value={inputValues.password}
                  onChange={handleChangeInputValues}
                  InputEndIcon={
                    showPass ? (
                      <RemoveRedEyeIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => setShowPass(false)}
                      />
                    ) : (
                      <VisibilityOffIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => setShowPass(true)}
                      />
                    )
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant={"outlined"}
            btnText={"cancel"}
            styleProps={{ width: 100, height: 40 }}
            handleClickBtn={handleClose}
          />
          <GlobalButton
            variant={"contained"}
            btnText={paramsData.type === "add" ? "addRoles" : "editRoles"}
            styleProps={{ width: 150, height: 40 }}
            handleClickBtn={handleAddEditUser}
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default AddEditAdminUserDialog;
